/**
 * "Địa điểm" overlay: shops, restaurants, schools, hospitals, offices… from Overture Maps Places
 * and OpenStreetMap, prebuilt into static JSON tiles by scripts/build-poi.py
 * (public/data/poi/manifest.json, a/<x>/<y>.json = z14 tiles with the important places,
 * b/<x>/<y>.json = z15 tiles with everything else). Rendered by PlanMap ('qh-poi'); each record
 * carries its own minimum zoom (15–18) so the map fills in progressively like Google Maps.
 * House numbers come from the OpenStreetMap vector tiles (PlanMap 'qh-housenumber').
 */
import { bus, prefs, savePrefs } from './state.js';
import { tilesForBBox } from './core/geo.js';
import { PLACE_GROUPS } from './placestyle.js';
import { vurl } from './assetv.js';

const A_ZOOM = 14.5, B_ZOOM = 16.5, MAX_TILES = 240;
const tiles = new Map(); // "a/x/y" | "b/x/y" -> Feature[]
const loading = new Set();
let pm = null, manifest = null, have = null, timer = 0;

export const placesOn = () => prefs.places !== false;

export function setPlaces(on) {
  prefs.places = !!on;
  savePrefs();
  pm?.setPlaces(prefs.places);
  bus.emit('places', prefs.places);
  refresh();
}

export function placesInfo() { return manifest ? { count: manifest.count, release: manifest.release } : null; }

async function loadManifest() {
  if (manifest) return manifest;
  const r = await fetch(vurl('/data/poi/manifest.json', 'poi'));
  if (!r.ok) throw new Error('poi manifest ' + r.status);
  manifest = await r.json();
  have = { a: new Set(manifest.a), b: new Set(manifest.b) };
  bus.emit('places-info', placesInfo());
  return manifest;
}

export function initPlaces(planMap) {
  pm = planMap;
  pm.setPlaces(placesOn());
  pm.map.on('moveend', () => { clearTimeout(timer); timer = setTimeout(refresh, 150); });
  pm.map.on('style.load', () => render());
  timer = setTimeout(refresh, 0);
}

function wanted() {
  const m = pm.map, z = m.getZoom();
  if (!placesOn() || z < A_ZOOM || !have) return [];
  const b = m.getBounds();
  const pad = 0.15;
  const dx = (b.getEast() - b.getWest()) * pad, dy = (b.getNorth() - b.getSouth()) * pad;
  const bb = [b.getWest() - dx, b.getSouth() - dy, b.getEast() + dx, b.getNorth() + dy];
  const out = [];
  for (const t of tilesForBBox(bb, 14).slice(0, 60)) if (have.a.has(`${t.x}/${t.y}`)) out.push(`a/${t.x}/${t.y}`);
  if (z >= B_ZOOM) for (const t of tilesForBBox(bb, 15).slice(0, 40)) if (have.b.has(`${t.x}/${t.y}`)) out.push(`b/${t.x}/${t.y}`);
  return out;
}

function toFeatures(rows) {
  const out = new Array(rows.length);
  for (let i = 0; i < rows.length; i++) {
    const [lng, lat, n, g, mz] = rows[i];
    out[i] = { type: 'Feature', properties: { n, g: PLACE_GROUPS[g]?.id || 'service', mz }, geometry: { type: 'Point', coordinates: [lng, lat] } };
  }
  return out;
}

let shown = '';
function render(keys = wanted()) {
  if (!pm) return;
  const ready = keys.filter((k) => tiles.has(k));
  const sig = ready.join('|');
  if (sig === shown && pm.data.poi?.features?.length) return;
  shown = sig;
  const features = [];
  for (const k of ready) for (const f of tiles.get(k)) features.push(f);
  pm.setData('poi', features.length ? { type: 'FeatureCollection', features } : null);
}

async function refresh() {
  if (!pm) return;
  if (!placesOn() || pm.map.getZoom() < A_ZOOM) { shown = ''; pm.setData('poi', null); return; }
  try { await loadManifest(); } catch { return; }
  const keys = wanted();
  render(keys);
  for (const k of keys) {
    if (tiles.has(k) || loading.has(k)) continue;
    loading.add(k);
    fetch(vurl(`/data/poi/${k}.json`, 'poi'))
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        tiles.set(k, toFeatures(Array.isArray(rows) ? rows : []));
        while (tiles.size > MAX_TILES) tiles.delete(tiles.keys().next().value);
        clearTimeout(renderTimer);
        renderTimer = setTimeout(() => render(), 60);
      })
      .catch(() => {})
      .finally(() => loading.delete(k));
  }
}
let renderTimer = 0;
